import { EventParam } from "@core/analytics/analytics-event";
import { ApiService, ErrorReporter } from "@core/api/api-service";
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export interface NetworkActionErrorHandler {
	clearError: () => void;
	reportError: (error: unknown, title?: string, message?: string) => void;
}

export interface NetworkAnalyticsHandler {
	trackError: (error: unknown, params?: EventParam[]) => void;
}

const defaultGetErrorReporter = () => false;
const defaultOtherErrorsReporter = () => true;

export class ApiServiceWithErrorHandler implements ApiService {
	constructor(
		private readonly innerApiService: ApiService,
		private readonly errorHandler: NetworkActionErrorHandler,
		private readonly analyticsHandler: NetworkAnalyticsHandler,
	) {}

	get axiosInstance(): AxiosInstance {
		return this.innerApiService.axiosInstance;
	}

	async get<T = unknown, R = AxiosResponse<T>>(
		url: string,
		config?: AxiosRequestConfig,
		reportError: ErrorReporter = defaultGetErrorReporter,
	): Promise<R> {
		try {
			this.errorHandler.clearError();
			return await this.innerApiService.get(url, config);
		} catch (error) {
			this.trackError(error, "GET", url);
			this.reportErrorOptional(error, reportError);
			throw error;
		}
	}

	async post<T = unknown, R = AxiosResponse<T>>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
		reportError: ErrorReporter = defaultOtherErrorsReporter,
	): Promise<R> {
		try {
			this.errorHandler.clearError();
			return await this.innerApiService.post(url, data, config);
		} catch (error) {
			this.trackError(error, "POST", url);
			this.reportErrorOptional(error, reportError);
			throw error;
		}
	}

	async put<T = unknown, R = AxiosResponse<T>>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
		reportError: ErrorReporter = defaultOtherErrorsReporter,
	): Promise<R> {
		try {
			this.errorHandler.clearError();
			return await this.innerApiService.put(url, data, config);
		} catch (error) {
			this.trackError(error, "PUT", url);
			this.reportErrorOptional(error, reportError);
			throw error;
		}
	}

	async delete<T = unknown, R = AxiosResponse<T>>(
		url: string,
		config?: AxiosRequestConfig,
		reportError: ErrorReporter = defaultOtherErrorsReporter,
	): Promise<R> {
		try {
			this.errorHandler.clearError();
			return await this.innerApiService.delete(url, config);
		} catch (error) {
			this.trackError(error, "DELETE", url);
			this.reportErrorOptional(error, reportError);
			throw error;
		}
	}

	private reportErrorOptional(error: unknown, reportErrors: ErrorReporter): void {
		if (reportErrors(error)) {
			this.errorHandler.reportError(error);
		}
	}

	private trackError(error: unknown, method: string, url: string): void {
		this.analyticsHandler.trackError(error, [method, url]);
	}
}
